import React, { memo, useEffect, useState } from "react";
import {
  CommericalAction,
  ICommercialsMenuItemProps,
  IDisabled,
  Menu,
} from "../../../types";
import styled from "styled-components";
import { selectionStore } from "../../../stores/selection.store";

import { pendingBookingStore } from "../../../stores/pending-booking.store";
import { useCommercialMenusContext } from "../../../contexts/commercial-context";
import { CommercialMenu } from "./commercial-menu";

const CommercialMenuCounter = styled.div`
  color: ${(props) => props.theme.textColor};
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.25);
  border-radius: 10px;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
  height: 24px;
  flex: auto 0 0;
  user-select: none;

  .hidden {
    display: none;
  }

  * {
    padding: 0 5px;
  }
`;

export const ChangedMenuCount = styled.div<IDisabled>`
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "inherit")};
`;

const Item = (props: ICommercialsMenuItemProps) => {
  const minimumDishes = 0;
  const [menusNumber, setMenusNumber] = useState(
    pendingBookingStore.selectedMenus.find(
      (menuItem) => menuItem.commercial_id === props.menu.id.toString()
    )?.quantity || 0
  );
  const [canLowerMenuCount, setCanLowerMenuCount] = useState(menusNumber > 0);
  const [canRaiseMenuCount, setCanRaiseMenuCount] = useState(true);
  const { menusAvailabilities, performAction } = useCommercialMenusContext();
  const [action, setAction] = useState<CommericalAction | null>(null);

  const setStepperButtons = (availableMenus: Array<Menu>, menuId: number) => {
    setCanLowerMenuCount(menusNumber > 0);
    setCanRaiseMenuCount(
      (pendingBookingStore.statisfySameMenusPaxCounterMandatoryForRaiseBtn &&
        availableMenus?.filter((menuItem) => menuItem.id === menuId).length >
          0) ||
        false
    );
  };

  useEffect(() => {
    setAction(null);
    setStepperButtons(
      pendingBookingStore.pendingBooking.available_menus,
      props.menu.id
    );
  }, [
    pendingBookingStore.pendingBooking.available_menus,
    selectionStore.menusCount,
  ]);

  useEffect(() => {
    if (action) {
      performAction(action, { [props.menu.id]: menusNumber });
    }
  }, [action]);

  const lowerMenusNumber = () => {
    if (menusNumber > minimumDishes) {
      const newMenuCount = menusNumber - 1;
      props.setChildLoading(true);

      setAction(
        newMenuCount === 0 ? CommericalAction.remove : CommericalAction.add
      );
      setMenusNumber(newMenuCount);
      selectionStore.updateSelectedMenus(props.menu.id, newMenuCount);
    }
  };
  const raiseMenusNumber = () => {
    const newMenuCount = menusNumber + 1;
    props.setChildLoading(true);
    setAction(CommericalAction.add);
    setMenusNumber(newMenuCount);
    selectionStore.updateSelectedMenus(props.menu.id, newMenuCount);
  };

  // Calculate canInteract once for both CommercialMenu and stepper buttons
  const canInteract =
    menusAvailabilities[props.menu.id] &&
    (props.menu.available_for_pending_booking ?? true);

  return (
    <CommercialMenu
      data-test={`commercial-menu-${props.menu.id}`}
      unavailable={!canRaiseMenuCount}
      unavailableReasonKey={
        pendingBookingStore.sameNumberMenu
          ? "instabook.menus.quantity_menu_match_pax_reached"
          : undefined
      }
      canInteract={canInteract}
      menu={props.menu}
      rightFooter={
        <CommercialMenuCounter data-test="menu-stepper">
          <ChangedMenuCount
            disabled={!canLowerMenuCount || !canInteract}
            onClick={lowerMenusNumber}
            data-test={`menus-lower-${props.menu.id}`}
          >
            -
          </ChangedMenuCount>
          <div data-test={`menus-number-${props.menu.id}`}>{menusNumber}</div>
          <ChangedMenuCount
            disabled={!canRaiseMenuCount || !canInteract}
            onClick={raiseMenusNumber}
            data-test={`menus-raise-${props.menu.id}`}
          >
            +
          </ChangedMenuCount>
        </CommercialMenuCounter>
      }
    />
  );
};

export const CommercialsMenuItem = memo(Item);
